import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import './ProjectPage.css';

function ProjectPage({ project, onBack }) {
  const getPreferredStartingIndex = (items = []) => {
    const videoIndex = items.findIndex((item) => item.type === 'video');
    return videoIndex !== -1 ? videoIndex : 0;
  };

  const [currentIndex, setCurrentIndex] = useState(() => getPreferredStartingIndex(project.images));
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isReady, setIsReady] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState('idle');
  const [pendingIndex, setPendingIndex] = useState(null);
  const transitionRef = useRef(null);

  const selectedItem = project.images[currentIndex] || project.images[0];
  const imageItems = project.images.filter((item) => item.type === 'image');

  useEffect(() => {
    setCurrentIndex(getPreferredStartingIndex(project.images));
    setTransitionPhase('idle');
    setIsTransitioning(false);
    setPendingIndex(null);
  }, [project.id, project.images]);

  useEffect(() => {
    let isCancelled = false;

    const preloadImages = async () => {
      setIsReady(false);

      await Promise.all(
        imageItems.map(
          (item) =>
            new Promise((resolve) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = resolve;
              img.src = item.url;
            })
        )
      );

      if (!isCancelled) {
        setIsReady(true);
      }
    };

    preloadImages();

    return () => {
      isCancelled = true;
    };
  }, [imageItems]);

  const goToIndex = useCallback(
    (nextIndex) => {
      if (nextIndex === currentIndex || isTransitioning || !isReady) return;

      setIsTransitioning(true);
      setTransitionPhase('out');
      setPendingIndex(nextIndex);
    },
    [currentIndex, isReady, isTransitioning]
  );

  const handleNext = useCallback(() => {
    const nextIndex = (currentIndex + 1) % project.images.length;
    goToIndex(nextIndex);
  }, [currentIndex, goToIndex, project.images.length]);

  const handlePrev = useCallback(() => {
    const prevIndex = currentIndex === 0 ? project.images.length - 1 : currentIndex - 1;
    goToIndex(prevIndex);
  }, [currentIndex, goToIndex, project.images.length]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleKeyDown = (event) => {
      if (!isReady || isTransitioning) return;
      if (event.key === 'ArrowLeft') {
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isReady, isTransitioning, handleNext, handlePrev]);

  useEffect(() => {
    return () => {
      if (transitionRef.current) {
        window.clearTimeout(transitionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (transitionPhase !== 'out' || pendingIndex === null) return;

    transitionRef.current = window.setTimeout(() => {
      setCurrentIndex(pendingIndex);
      setPendingIndex(null);
      setTransitionPhase('in');
    }, 150);

    return () => {
      if (transitionRef.current) {
        window.clearTimeout(transitionRef.current);
      }
    };
  }, [transitionPhase, pendingIndex]);

  useEffect(() => {
    if (transitionPhase !== 'in') return;

    transitionRef.current = window.setTimeout(() => {
      setTransitionPhase('idle');
      setIsTransitioning(false);
    }, 150);

    return () => {
      if (transitionRef.current) {
        window.clearTimeout(transitionRef.current);
      }
    };
  }, [transitionPhase]);

  const handleTouchStart = (event) => {
    setTouchStart(event.targetTouches[0].clientX);
  };

  const handleTouchMove = (event) => {
    setTouchEnd(event.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      handleNext();
    } else if (touchEnd - touchStart > 50) {
      handlePrev();
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.split('v=')[1];
    return `https://www.youtube.com/embed/${videoId}`;
  };

  const renderWritingParagraph = (paragraph, index) => (
    <p key={index}>
      {paragraph.split(/(\*\*.*?\*\*)/g).map((part, partIndex) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={`${index}-${partIndex}`}>{part.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={`${index}-${partIndex}`}>{part}</React.Fragment>;
      })}
    </p>
  );

  return (
    <div className="project-page-container">
      <button className="back-button" onClick={onBack}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5"></path>
          <path d="M12 5l-7 7 7 7"></path>
        </svg>
      </button>

      <div className="content-wrapper">
        <div className="project-main-column">
          <div className="gallery-section">
            <div
              className="main-image-container"
              onTouchStart={isMobile ? handleTouchStart : undefined}
              onTouchMove={isMobile ? handleTouchMove : undefined}
              onTouchEnd={isMobile ? handleTouchEnd : undefined}
            >
              {!isReady && imageItems.length > 0 ? (
                <div className="main-image-loading" aria-live="polite">
                  <div className="loading-spinner" />
                </div>
              ) : (
                <>
                  <motion.div
                    className="main-blackout"
                    animate={{ opacity: transitionPhase === 'idle' ? 0 : 1 }}
                    transition={{ duration: 0.16, ease: 'easeInOut' }}
                  />

                  <motion.div
                    key={currentIndex}
                    className="main-media"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: transitionPhase === 'in' || transitionPhase === 'idle' ? 1 : 0 }}
                    transition={{ duration: 0.16, ease: 'easeInOut' }}
                  >
                    {selectedItem.type === 'video' ? (
                      <iframe
                        src={getEmbedUrl(selectedItem.url)}
                        title="Project Video"
                        className="main-video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        style={{ width: '90%', height: '90%', border: 'none' }}
                        allowFullScreen
                      ></iframe>
                    ) : selectedItem.type === 'website' ? (
                      <iframe
                        src={selectedItem.url}
                        title="Website Preview"
                        className="main-website"
                        allowFullScreen
                        style={{ width: '90%', height: '90%', border: 'none' }}
                      ></iframe>
                    ) : (
                      <img
                        src={selectedItem.url}
                        alt={`${project.title} ${currentIndex + 1}`}
                        className="main-image"
                      />
                    )}
                  </motion.div>
                </>
              )}

              <button className="nav-button prev" onClick={handlePrev} disabled={!isReady || isTransitioning}>
                &lt;
              </button>
              <button className="nav-button next" onClick={handleNext} disabled={!isReady || isTransitioning}>
                &gt;
              </button>
            </div>

            {!isMobile && (
              <div className="thumbnail-container">
                {project.images.map((item, index) => (
                  <motion.div
                    key={index}
                    className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToIndex(index)}
                  >
                    {item.type === 'video' ? (
                      <div className="video-thumbnail">
                        <img
                          src={`https://img.youtube.com/vi/${item.url.split('v=')[1]}/0.jpg`}
                          alt="Video Thumbnail"
                          className="thumbnail-image"
                        />
                        <span className="play-icon">▶</span>
                      </div>
                    ) : item.type === 'website' ? (
                      <div className="website-thumbnail">
                        <span className="website-icon">🌐</span>
                      </div>
                    ) : (
                      <img
                        src={item.url}
                        alt={`${project.title} ${index + 1}`}
                        className="thumbnail-image"
                      />
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>

        </div>

        <div className={`project-details ${isMobile ? 'mobile' : ''}`}>
          <div className="title-row">
            <h1>{project.title}</h1>
            <p className="project-date">{project.date}</p>
          </div>
          <p className="project-summary">{project.summary}</p>

          {project.writing && (
            <div className="project-writing">
              {project.writing.split(/\n\s*\n/).map((paragraph, index) => renderWritingParagraph(paragraph, index))}
            </div>
          )}

          {project.paragraph && <div className="project-paragraph">{project.paragraph}</div>}

          {project.link && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link-button"
            >
              Visit Project
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectPage;
